import { motion } from "motion/react"
import { Link } from "react-router-dom"
import { Monitor, Moon, Sun } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTheme } from "@/components/theme-provider"

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12">
      <BackgroundEffects />

      <ThemeToggle />

      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm"
      >
        {children}
      </motion.main>

      <FooterChrome />
    </div>
  )
}

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme()
  const Icon = resolvedTheme === "light" ? Sun : Moon
  return (
    <div className="absolute right-5 top-5 z-20">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Switch theme"
            className="h-8 w-8 text-muted-foreground/70 hover:bg-foreground/4 hover:text-foreground"
          >
            <Icon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-36">
          <DropdownMenuItem onClick={(e) => setTheme("light", { x: e.clientX, y: e.clientY })}>
            <Sun className="mr-2 h-4 w-4" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => setTheme("dark", { x: e.clientX, y: e.clientY })}>
            <Moon className="mr-2 h-4 w-4" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => setTheme("system", { x: e.clientX, y: e.clientY })}>
            <Monitor className="mr-2 h-4 w-4" /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function BackgroundEffects() {
  const { resolvedTheme } = useTheme()
  const hexPattern =
    resolvedTheme === "light"
      ? "url('/hex-pattern-light.svg')"
      : "url('/hex-pattern.svg')"

  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-48 left-1/2 h-96 w-3xl -translate-x-1/2 rounded-full bg-rose-500/15 blur-[120px] dark:bg-rose-500/10"
      />

      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-48 h-96 w-96 rounded-full bg-rose-500/5 blur-[100px]"
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-8 dark:opacity-6"
        style={{ backgroundImage: hexPattern, backgroundSize: "56px 100px" }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-4 dark:opacity-[0.025]"
        style={{
          backgroundImage:
            "radial-gradient(circle, currentColor 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-linear-to-t from-background to-transparent"
      />
    </>
  )
}

function FooterChrome() {
  return (
    <div className="absolute inset-x-0 bottom-5 z-10 flex items-center justify-center gap-3 text-2xs uppercase tracking-[0.18em] text-muted-foreground/40">
      <Link
        to="/login"
        className="transition-colors hover:text-foreground/60"
      >
        Sign in
      </Link>
      <span className="opacity-50">·</span>
      <a
        href="https://hivehook.com"
        target="_blank"
        rel="noopener noreferrer"
        className="transition-colors hover:text-foreground/60"
      >
        hivehook.com
      </a>
      <span className="opacity-50">·</span>
      <a
        href="https://hivehook.com/docs"
        target="_blank"
        rel="noopener noreferrer"
        className="transition-colors hover:text-foreground/60"
      >
        Docs
      </a>
    </div>
  )
}
